import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { PaperPlaneRight, Warning, CalendarBlank, Database } from '@phosphor-icons/react';

const AnswerCard = ({ a, i }) => (
  <Card data-testid={`assistant-answer-${i}`}>
    <CardHeader className="pb-2">
      <p className="text-xs text-muted-foreground" data-testid={`assistant-question-${i}`}>Soru: {a.question}</p>
      <CardTitle className="text-base">{a.title}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3 text-sm">
      <p className="font-medium" data-testid={`assistant-answer-text-${i}`}>{a.answer}</p>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
        {a.period && <span className="flex items-center gap-1" data-testid={`assistant-period-${i}`}><CalendarBlank size={14} />{a.period.label}{a.period.start ? ` (${a.period.start}${a.period.end && a.period.end !== a.period.start ? ` – ${a.period.end}` : ''})` : ''}</span>}
        {a.coverage && <span className="flex items-center gap-1" data-testid={`assistant-coverage-${i}`}><Database size={14} />{a.coverage}</span>}
      </div>
      {a.table && a.table.rows.length > 0 && (
        <div className="max-h-72 overflow-auto border rounded-md">
          <Table className="text-xs">
            <TableHeader><TableRow>{a.table.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{a.table.rows.map((r, j) => <TableRow key={j}>{r.map((c, k) => <TableCell key={k}>{c}</TableCell>)}</TableRow>)}</TableBody>
          </Table>
        </div>
      )}
      {(a.extra_tables || []).map((t, j) => (
        <div key={t.title} data-testid={`assistant-extra-table-${i}-${j}`}>
          <p className="text-xs font-medium mb-1">{t.title}</p>
          <div className="border rounded-md overflow-auto">
            <Table className="text-xs">
              <TableHeader><TableRow>{t.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
              <TableBody>{t.rows.map((r, k) => <TableRow key={k}>{r.map((c, m) => <TableCell key={m}>{c}</TableCell>)}</TableRow>)}</TableBody>
            </Table>
          </div>
        </div>
      ))}
      {a.warnings.map((w) => (
        <p key={w} className="flex gap-2 text-xs text-amber-700"><Warning size={14} className="shrink-0 mt-0.5" />{w}</p>
      ))}
    </CardContent>
  </Card>
);

export const ManagerAssistant = () => {
  const [examples, setExamples] = useState([]);
  const [q, setQ] = useState('');
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastQ, setLastQ] = useState('');
  const [group, setGroup] = useState(0);

  useEffect(() => { api.get('/assistant/examples').then((r) => setExamples(r.data)).catch(() => {}); }, []);

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (text.length < 2 || loading) return;
    setLoading(true);
    setError(null);
    setLastQ(text);
    try {
      const r = await api.post('/assistant/ask', { question: text }, { timeout: 60000 });
      setAnswers((prev) => [{ ...r.data, question: text }, ...prev]);
      setQ('');
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Cevap alınamadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="manager-assistant">
      <Card>
        <CardContent className="pt-6 space-y-3">
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); ask(); }}>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sorunuzu yazın, örn: Dün ne kadar ciro yaptık?" maxLength={300} data-testid="assistant-input" />
            <Button type="submit" disabled={loading || q.trim().length < 2} data-testid="assistant-send-btn">
              <PaperPlaneRight size={16} className="mr-2" />{loading ? 'Hesaplanıyor...' : 'Sor'}
            </Button>
          </form>
          <div className="flex flex-wrap gap-1 border-b pb-2">
            {examples.map((g, i) => (
              <Button key={g.group} size="sm" variant={group === i ? 'default' : 'ghost'} className="h-7 text-xs" onClick={() => setGroup(i)} data-testid={`assistant-group-${i}`}>{g.group}</Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {(examples[group]?.questions || []).map((e, i) => (
              <Button key={e} size="sm" variant="outline" className="text-xs h-auto py-1" disabled={loading} onClick={() => ask(e)} data-testid={`assistant-example-${i}`}>{e}</Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Cevaplar kayıtlı ERP ve Orion POS verilerinden kural tabanlı hesaplanır; tahmin yapılmaz, kayıt değiştirilmez.</p>
        </CardContent>
      </Card>
      {error && (
        <div className="p-3 border border-destructive rounded-lg text-destructive text-sm flex items-center gap-3" data-testid="assistant-error">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={() => ask(lastQ)} data-testid="assistant-retry-btn">Yeniden Dene</Button>
        </div>
      )}
      {answers.map((a, i) => <AnswerCard key={answers.length - i} a={a} i={i} />)}
    </div>
  );
};
